import { useState } from "react"
import { NavLink, Outlet } from "react-router-dom"
import { LayoutDashboard, FileText, Settings, MapPin, LogOut, Menu, X, User } from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuth } from "@/contexts/AuthContext"
import { usePublisherProfile } from "@/hooks/usePortal"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

const BASE_NAV_ITEMS = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/leads", label: "Leads", icon: FileText, end: false },
]

const SETTINGS_NAV_ITEM = { to: "/settings", label: "Settings", icon: Settings, end: false }
const DEMAND_NAV_ITEM = { to: "/demand", label: "Demand", icon: MapPin, end: false }

export function Layout() {
  const { email, name, logout, versions, publisherId, switchVersion } = useAuth()
  const { data: profile } = usePublisherProfile()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [switching, setSwitching] = useState(false)

  const handleSwitch = async (pid: string) => {
    if (pid === publisherId) return
    setSwitching(true)
    try {
      await switchVersion(pid)
    } finally {
      setSwitching(false)
    }
  }

  const VersionSwitcher = versions.length > 1 ? (
    <Select value={publisherId ?? undefined} onValueChange={handleSwitch} disabled={switching}>
      <SelectTrigger className="h-8 w-[150px] text-xs">
        <SelectValue placeholder="Version" />
      </SelectTrigger>
      <SelectContent>
        {versions.map((v) => (
          <SelectItem key={v.publisher_id} value={v.publisher_id}>{v.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : null

  // Settings (API Keys + API Docs) is for API publishers; Demand (high-value ZIPs)
  // is for non-API publishers. Show one or the other based on API access.
  const apiAccessEnabled = profile?.api_access_enabled !== false
  const NAV_ITEMS = apiAccessEnabled
    ? [...BASE_NAV_ITEMS, SETTINGS_NAV_ITEM]
    : [...BASE_NAV_ITEMS, DEMAND_NAV_ITEM]

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-6">
              <span className="text-sm font-semibold text-gradient">Publisher Portal</span>
              <nav className="hidden md:flex items-center gap-1">
                {NAV_ITEMS.map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.end}
                    className={({ isActive }) => cn(
                      "flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors",
                      isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    )}>
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
            <div className="hidden md:flex items-center gap-3">
              {VersionSwitcher}
              <NavLink
                to="/account"
                title="Account settings"
                className={({ isActive }) => cn(
                  "px-2 py-1 text-xs rounded-md transition-colors",
                  isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}>
                {name || email}
              </NavLink>
              <Button variant="ghost" size="sm" onClick={logout} className="h-8 gap-1.5 text-xs text-muted-foreground">
                <LogOut className="h-3.5 w-3.5" />
                Logout
              </Button>
            </div>
            <button className="md:hidden p-2 text-muted-foreground" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-border p-4 space-y-2">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setMobileOpen(false)}
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 text-sm rounded-md",
                  isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground"
                )}>
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
            <div className="border-t border-border pt-2 mt-2">
              {versions.length > 1 && (
                <div className="px-3 mb-3">{VersionSwitcher}</div>
              )}
              <NavLink to="/account" onClick={() => setMobileOpen(false)}
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 text-sm rounded-md",
                  isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground"
                )}>
                <User className="h-4 w-4" /> {name || email}
              </NavLink>
              <button onClick={logout} className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground w-full">
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          </div>
        )}
      </header>
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <Outlet />
      </main>
    </div>
  )
}
